import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Stack, Stepper, Step, StepLabel, Button, Typography, Divider, Box, StepButton } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

/**
 * WizardStepper Component
 *
 * Step object structure:
 * {
 *   label: string,                    // Display name of the step
 *   showStepper: boolean,            // Whether to show the stepper navigation
 *   stepOptional: boolean,           // Whether the step is optional
 *   footerButtonTitles: [string, string], // [primaryButton, backButton] text
 *   contentComponent: ReactElement,  // The form content for this step
 *   shouldCheckBuyEligibility?: boolean, // Whether to run buy eligibility check on next/finish
 *   showEarlySubmitButton?: boolean, // Whether to show an early submit button on this step
 *   earlySubmitButtonLabel?: string,      // Custom label for the early submit button (defaults to 'FINISH')
 * }
 */

const WizardStepper = ({
  steps,
  onHideModal,
  initialStep,
  nonLinear,
  title,
  validationCheck,
  onValidatePartial,
  onSubmitPartial,
  canArchive,
  handleArchiveClick,
  isWide,
  disableAlertModal,
  requestCheckBuyEligibility,
  isCheckingEligibility,
  hasDoNotBuyContacts,
  onEarlySubmit,
}) => {
  const [activeStep, setStep] = React.useState(initialStep)

  const [pendingStep, setPendingStep] = React.useState(initialStep)

  const [showChangeModal, setShowChangeModal] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)

  const validateDirty = async () => {
    const shouldDisable = await onValidatePartial(activeStep)
    setIsDisabled(shouldDisable)
    return null
  }

  /* Returns true if the step has unsaved data */
  const isStepDirty = step => {
    return !onValidatePartial(step)
  }
  const setActiveStep = step => {
    if (isStepDirty(activeStep)) {
      setPendingStep(step)
      setShowChangeModal(true)
    } else {
      setStep(step)
    }
  }

  const handleStepAction = async (e, onSuccess) => {
    e.preventDefault()

    const currentStep = steps[activeStep]

    if (validationCheck) {
      const isValid = await onValidatePartial(activeStep)
      if (isValid) {
        if (currentStep?.shouldCheckBuyEligibility) {
          requestCheckBuyEligibility()
        }
        onSuccess()
      }
    } else {
      if (currentStep?.shouldCheckBuyEligibility) {
        requestCheckBuyEligibility()
      }
      onSuccess()
    }
  }

  const handleNext = async e => {
    await handleStepAction(e, () => {
      if (activeStep < steps.length - 1) {
        setActiveStep(prevActiveStep => prevActiveStep + 1)
      }
    })
  }

  const handleBack = () => {
    if (activeStep !== 0) {
      setActiveStep(prevActiveStep => prevActiveStep - 1)
    } else if (disableAlertModal) {
      onHideModal()
    } else {
      setShowCloseModal(true)
    }
  }

  const handleEarlySubmit = async e => {
    await handleStepAction(e, () => {
      if (onEarlySubmit) {
        onEarlySubmit(e)
      }
    })
  }

  const [isDisabled, setIsDisabled] = useState(true)

  const footerContent = () => {
    if (nonLinear) {
      validateDirty()
      return (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              disabled={isDisabled || isCheckingEligibility || hasDoNotBuyContacts}
              loading={isCheckingEligibility}
              onClick={() => onSubmitPartial(activeStep)}
            >
              Save Changes
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={() => {
                const hasUnsavedData = steps.some((step, index) => isStepDirty(index))
                if (!hasUnsavedData || disableAlertModal) {
                  onHideModal()
                } else {
                  setShowCloseModal(true)
                }
              }}
            >
              Cancel
            </Button>
          </Stack>

          {canArchive && (
            <Button variant="outlined" color="error" onClick={handleArchiveClick}>
              Archive
            </Button>
          )}
        </Stack>
      )
    }

    const currentStep = steps[activeStep]
    const hasEarlySubmitButton = currentStep.showEarlySubmitButton && onEarlySubmit

    return (
      <>
        {activeStep === steps.length - 1 ? (
          <LoadingButton
            type="submit"
            disabled={isCheckingEligibility || hasDoNotBuyContacts}
            loading={isCheckingEligibility}
            variant="contained"
          >
            {currentStep.footerButtonTitles[0]}
          </LoadingButton>
        ) : (
          <Button variant="contained" onClick={handleNext}>
            {currentStep.footerButtonTitles[0]}
          </Button>
        )}
        <Button variant="outlined" onClick={handleBack}>
          {currentStep.footerButtonTitles[1]}
        </Button>
        {hasEarlySubmitButton && (
          <Button
            disabled={isCheckingEligibility || hasDoNotBuyContacts}
            variant="outlined"
            onClick={handleEarlySubmit}
          >
            {currentStep.earlySubmitButtonLabel || 'FINISH'}
          </Button>
        )}
      </>
    )
  }

  return (
    <div>
      <AlertModal
        open={showChangeModal}
        modalType="close"
        title="Leave this section"
        question="Are you sure you want to leave this section?"
        description="You still have unsaved data on this section and all unsaved data on it will be lost if you proceed."
        confirmLabel="YES, LEAVE SECTION"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowChangeModal(false)
        }}
        onSubmit={() => {
          setShowChangeModal(false)
          setStep(pendingStep)
        }}
      />
      <AlertModal
        open={showCloseModal}
        modalType="close"
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="You have unsaved data on this page and all unsaved data on the ticket will be lost."
        confirmLabel="YES, LEAVE PAGE"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowCloseModal(false)
        }}
        onSubmit={() => {
          setShowCloseModal(false)
          onHideModal()
        }}
      />
      <Stack height="100vh">
        <FormModalHeading
          title={title}
          backButtonPosition="right"
          onBack={() => {
            if (disableAlertModal || (nonLinear && isDisabled)) {
              onHideModal()
            } else {
              setShowCloseModal(true)
            }
          }}
        />

        <Divider />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          flexGrow={1}
          overflow="auto"
          divider={steps[activeStep].showStepper && <Divider orientation="vertical" flexItem />}
        >
          {steps[activeStep].showStepper && (
            <Box width={{ xs: '100%', md: '25%' }} pt={4} pl={4}>
              <Stepper orientation="vertical" activeStep={activeStep} nonLinear={nonLinear}>
                {steps.map((item, index) => {
                  const labelProps = {}
                  if (steps[index].stepOptional) {
                    labelProps.optional = <Typography variant="caption">Optional</Typography>
                  }
                  return (
                    <Step key={item.label}>
                      {index === activeStep ? (
                        <StepLabel {...labelProps}>{item.label}</StepLabel>
                      ) : (
                        <StepButton
                          color="inherit"
                          onClick={() => {
                            setActiveStep(index)
                          }}
                        >
                          <StepLabel {...labelProps}>{item.label}</StepLabel>
                        </StepButton>
                      )}
                    </Step>
                  )
                })}
              </Stepper>
            </Box>
          )}
          <Box width={{ xs: '100%', md: isWide ? '100%' : '75%' }} pt={4}>
            {steps[activeStep].contentComponent}
          </Box>
        </Stack>

        <Stack
          direction="row"
          p={2}
          spacing={2}
          sx={{
            boxShadow:
              '0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 1px 8px rgba(0, 0, 0, 0.5)',
          }}
        >
          {footerContent()}
        </Stack>
      </Stack>
    </div>
  )
}

const customValidator = (props, propName) => {
  const prop = props[propName]
  if (props.nonLinear && (prop === undefined || typeof prop !== 'function')) {
    return new Error(`If nonLinear is true, ${propName} is a required function`)
  }
  return null
}

WizardStepper.propTypes = {
  steps: PropTypes.array.isRequired,
  onHideModal: PropTypes.func,
  initialStep: PropTypes.number,
  nonLinear: PropTypes.bool,
  title: PropTypes.string,
  validationCheck: PropTypes.bool,
  onValidatePartial: PropTypes.func.isRequired,

  onSubmitPartial: (props, propName) => customValidator(props, propName),
  canArchive: PropTypes.bool,
  handleArchiveClick: PropTypes.func,
  isWide: PropTypes.bool,
  disableAlertModal: PropTypes.bool,
  requestCheckBuyEligibility: PropTypes.func,
  isCheckingEligibility: PropTypes.bool,
  hasDoNotBuyContacts: PropTypes.bool,
  onEarlySubmit: PropTypes.func,
}

WizardStepper.defaultProps = {
  onHideModal: null,
  initialStep: 0,
  nonLinear: false,
  title: '',
  validationCheck: false,
  canArchive: false,
  handleArchiveClick: () => {},
  isWide: false,
  disableAlertModal: false,
  requestCheckBuyEligibility: () => {},
  isCheckingEligibility: false,
  hasDoNotBuyContacts: false,
  onEarlySubmit: null,
}

export default WizardStepper
